import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import React, {Component, createRef, Fragment, RefObject} from 'react';
import styled, { ThemeProps } from 'styled-components';
import IfSocialLinksAvailable from '../../components/behavior/IfSocialLinksAvailable';
import { HeaderMobile } from '../../components/Header';
import {
  NavigationBGMobile,
  NavigationCopyAlertMobile,
  NavigationWrapperMobile
} from '../../components/NavigationMobile';
import { Section } from '../../components/Section';
import { rgba } from '../../components/utils/rgba';
import { withErrorBoundary } from '../../error-handling/withErrorBoundary';
import {
  selectIsPathContentLoaded,
  selectIsStaticContentLoaded,
  selectLayer,
  selectMenu,
  selectMeta
} from '../../provider/Typo3Provider/selectors';
import Typo3Label from '../../provider/Typo3Provider/Typo3Label';
import {
  withTypo3,
  WithTypo3Props
} from '../../provider/Typo3Provider/withTypo3';
import { Theme } from '../../theme';
import { StyledDivComponent } from '../../types';
import IntroSearch from '../IntroSearch';
import DataLayerContainerMobile from './DataLayerContainerMobile';
import Hamburger from './Hamburger';
import InfoLink from './InfoLink';
import LogoContainer from './LogoContainer';
import MetaNavigationContainerMobile from './MetaNavigationContainerMobile';
import NavigationContainerMobile from './NavigationContainerMobile';
import ShareButton from './ShareButton';
import ShareLayerContainerMobile from "./ShareLayerContainerMobile";
import {AnniversaryLogo} from '../../components/AnniversaryLogo';

interface OverflowContainerProps {
  open?: boolean;
}

interface OverflowContainerPropsThemed
  extends ThemeProps<Theme>,
    OverflowContainerProps {}

const OverflowContainer: StyledDivComponent<
  OverflowContainerProps
> = styled.div`
  flex: 1;
  overflow: auto;
  /* position: fixed; */
  width: 100%;
  /* top: 60px;
  bottom: 0; */
  z-index: 190;
  pointer-events: ${(props: OverflowContainerPropsThemed) =>
    props.open ? 'auto' : 'none'};
  -webkit-overflow-scrolling: touch;
  background: ${(props: OverflowContainerPropsThemed) =>
    props.theme.colors.white};
  transform: ${(props: OverflowContainerPropsThemed) =>
    props.open ? 'translateX(0)' : 'translateX(100%)'};
  transition: transform 0.5s ease-in-out;
`;

interface ContainerProps {
  fullscreen?: boolean;
}

interface ContainerPropsThemed extends ThemeProps<Theme>, ContainerProps {}

const Container: StyledDivComponent<ContainerProps> = styled.div`
  box-shadow: 3px 3px 5px 0px
    ${(props: ContainerPropsThemed) => rgba(props.theme.colors.black, 0.2)};
  display: flex;
  flex-direction: column;
  position: fixed;
  top: 0;
  width: 100%;
  height: ${(props: ContainerPropsThemed) =>
    props.fullscreen ? '100%' : 'auto'};
  z-index: 200;

  + main {
    margin-top: 60px;
    display: block;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

interface HeaderContainerState {
  activeMenu: boolean;
  activeLayer: boolean;
  activeShare: boolean;
  showCopyAlertTimeout?: number | undefined;
  pageId: number;
}

interface HeaderContainerProps {}

class HeaderContainer extends Component<
  HeaderContainerProps & WithTypo3Props,
  HeaderContainerState
> {
  public static getDerivedStateFromProps({
    typo3
  }: HeaderContainerProps & WithTypo3Props): Partial<
    HeaderContainerState
  > | null {
    const meta = selectMeta(typo3);
    if (meta) {
      return {
        pageId: meta.page.uid
      };
    }
    return null;
  }

  public state = {
    activeMenu: false,
    activeLayer: false,
    activeShare: false,
    showCopyAlertTimeout: undefined,
    pageId: -1
  };

  public overlayRef = createRef<HTMLDivElement>();
  public containerRef = createRef<HTMLDivElement>();

  public enableBodyScroll({ current }: RefObject<HTMLDivElement>) {
    current && enableBodyScroll(current);
  }

  public disableBodyScroll({ current }: RefObject<HTMLDivElement>) {
    current &&
      disableBodyScroll(current, {
        reserveScrollBarGap: true
      });
  }

  public toggleHamburger = () => {
    const { activeLayer, activeShare } = this.state;
    if (activeLayer) {
      this.toggleDataLayer();
    } else if (activeShare) {
      this.toggleShareLayer();
    }
    this.setState(({ activeMenu }) => ({ activeMenu: !activeMenu }));
  };

  public toggleDataLayer = () => {
    const { activeMenu, activeShare } = this.state;
    if (activeMenu) {
      this.toggleHamburger();
    } else if (activeShare) {
      this.toggleShareLayer();
    }
    this.setState(({ activeLayer }) => ({ activeLayer: !activeLayer }));
  };

  public toggleShareLayer = () => {
    const { activeLayer, activeMenu } = this.state;
    if (activeLayer) {
      this.toggleDataLayer();
    } else if (activeMenu) {
      this.toggleHamburger();
    }
    this.setState(({ activeShare }) => ({ activeShare: !activeShare }));
  };

  public toggleOverlay = () => {
    const { activeLayer, activeShare } = this.state;
    if (activeLayer) {
      this.toggleDataLayer();
      this.setState(({ activeLayer }) => ({ activeLayer: false }));
    } else if (activeShare) {
      this.toggleShareLayer();
      this.setState(({ activeShare }) => ({ activeShare: false }));
    }
  };

  public componentDidUpdate(_: any, prevState: HeaderContainerState) {
    const { activeMenu, activeLayer, activeShare } = this.state;
    const menuBecameActive = activeMenu && !prevState.activeMenu;
    const menuBecameInactive = !activeMenu && prevState.activeMenu;
    const layerBecameActive = activeLayer && !prevState.activeLayer;
    const layerBecameInactive = !activeLayer && prevState.activeLayer;
    const shareBecameInactive = !activeShare && prevState.activeShare;
    if (menuBecameInactive) {
      this.enableBodyScroll(this.overlayRef);
      this.enableBodyScroll(this.containerRef);
    } else if (menuBecameActive) {
      this.disableBodyScroll(this.overlayRef);
      this.disableBodyScroll(this.containerRef);
    }
    if (layerBecameInactive) {
      this.enableBodyScroll(this.containerRef);
    } else if (layerBecameActive) {
      this.disableBodyScroll(this.containerRef);
    }
    if (this.state.pageId !== prevState.pageId) {
      const { activeMenu } = this.state;
      if (activeMenu) {
        this.toggleHamburger();
      }
    }
    if (shareBecameInactive && this.state.showCopyAlertTimeout) {
      this.setState({showCopyAlertTimeout: undefined});
      window.clearTimeout(this.state.showCopyAlertTimeout);
    }
  }

  public componentWillUnmount() {
    this.enableBodyScroll(this.overlayRef);
    this.enableBodyScroll(this.containerRef);
    if (this.state.showCopyAlertTimeout) {
      window.clearTimeout(this.state.showCopyAlertTimeout);
    }
  }

  public onCopiedToClipboard = () => {
    if (this.state.showCopyAlertTimeout) {
      return;
    }

    const showCopyAlertTimeout = window.setTimeout(() => {
      this.setState({showCopyAlertTimeout: undefined});
    }, 5000);
    this.setState({showCopyAlertTimeout});
  };

  public render() {
    const { typo3 } = this.props;
    const isStaticContentLoaded = selectIsStaticContentLoaded(typo3);

    if (!isStaticContentLoaded) {
      return null;
    }

    const isContentLoaded =
      selectIsPathContentLoaded(typo3) && isStaticContentLoaded;
    const [rootPage = null] = selectMenu(this.props.typo3, 'rootpage');
    const { activeMenu, activeLayer, activeShare } = this.state;

    const layer = selectLayer(typo3);

    return (
      <Container
        innerRef={this.containerRef}
        fullscreen={isContentLoaded && (activeLayer || activeMenu || activeShare)}
      >
        <HeaderMobile closed={false /*!(activeMenu || activeLayer || activeShare)*/}>
          <Section>
            <LogoContainer root={rootPage || undefined} />
            {/* <AnniversaryLogo /> */}
            <IfSocialLinksAvailable>
              <ShareButton active={activeShare} onClick={this.toggleShareLayer}/>
            </IfSocialLinksAvailable>
            <InfoLink active={activeLayer} onClick={this.toggleDataLayer} />
            <Hamburger active={activeMenu} onClick={this.toggleHamburger} />
          </Section>
        </HeaderMobile>
        <HeaderContent>
          <NavigationBGMobile show={activeLayer || activeShare} onClick={this.toggleOverlay}>
            {this.state.showCopyAlertTimeout && (
              <NavigationCopyAlertMobile>
                <Typo3Label label="stickySideAlert.copy" />
              </NavigationCopyAlertMobile>
            )}
          </NavigationBGMobile>
          {layer && (
            <DataLayerContainerMobile layer={layer} show={activeLayer} />
          )}
          <ShareLayerContainerMobile show={activeShare} onCopiedToClipboard={this.onCopiedToClipboard} />
          <OverflowContainer innerRef={this.overlayRef} open={activeMenu}>
            {activeMenu && (
              <NavigationWrapperMobile open>
                {isContentLoaded && (
                  <Fragment>
                    <IntroSearch mobile onClick={this.toggleHamburger} />
                    <NavigationContainerMobile
                      onClickChild={this.toggleHamburger}
                    />
                    <MetaNavigationContainerMobile
                      onClickChild={this.toggleHamburger}
                    />
                  </Fragment>
                )}
              </NavigationWrapperMobile>
            )}
          </OverflowContainer>
        </HeaderContent>
      </Container>
    );
  }
}

export default withErrorBoundary(
  withTypo3(HeaderContainer),
  'HeaderContainerMobile'
);
